import { type FindingContentInput, isMemberId } from "@repo/domain";
import { z } from "zod";
import { memberIdSchema, parameterValueSchema } from "./common.schemas";
import { newCanonicalSchema } from "./entity-id.schemas";

const clientOptionIdSchema = z.string().regex(/^option-[0-9a-f-]{36}$/);

const optionSchema = z.object({
  id: z.union([memberIdSchema, clientOptionIdSchema]).optional(),
  label: z.string().trim().min(1),
  detail: z.string().trim().min(1).optional(),
  normal: z.boolean().optional(),
});

const choiceContent = z.object({
  kind: z.literal("Choice"),
  cardinality: z.enum(["Single", "Multiple"]),
  offerOther: z.boolean().optional(),
  options: z.array(optionSchema).min(1),
});

const measuredContent = z.object({
  kind: z.literal("Measured"),
  components: z
    .array(z.object({ id: memberIdSchema.optional(), label: z.string().trim().min(1), value: parameterValueSchema }))
    .min(1),
});
const defineFields = {
  definitionUrl: newCanonicalSchema,
  name: z.string().trim().min(1),
  description: z.string().trim().min(1).optional(),
};
const reviseFields = { definitionUrl: z.string().url(), expectedVersion: z.number().int().positive() };
export const defineFindingDefinitionInputSchema = z.discriminatedUnion("kind", [
  choiceContent.extend(defineFields),
  measuredContent.extend(defineFields),
]);
export type DefineFindingDefinitionInput = z.infer<typeof defineFindingDefinitionInputSchema>;
export const reviseFindingDefinitionInputSchema = z.discriminatedUnion("kind", [
  choiceContent.extend(reviseFields),
  measuredContent.extend(reviseFields),
]);
export type ReviseFindingDefinitionInput = z.infer<typeof reviseFindingDefinitionInputSchema>;

export const correctFindingDefinitionInputSchema = z.object({
  definitionUrl: z.string().url(),
  name: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1).nullable().optional(),
});
export type CorrectFindingDefinitionInput = z.infer<typeof correctFindingDefinitionInputSchema>;

export const changeFindingDefinitionAvailabilityInputSchema = z.object({
  definitionUrl: z.string().url(),
});
export type ChangeFindingDefinitionAvailabilityInput = z.infer<typeof changeFindingDefinitionAvailabilityInputSchema>;

export function findingContentToDomain(
  input: ReviseFindingDefinitionInput | DefineFindingDefinitionInput,
): FindingContentInput {
  if (input.kind === "Measured")
    return {
      kind: "Measured",
      components: input.components.map((component) => ({
        ...component,
        id: isMemberId(component.id) ? component.id : undefined,
        value:
          component.value.kind === "Choice"
            ? {
                ...component.value,
                options: component.value.options.map((option) => ({
                  ...option,
                  id: isMemberId(option.id) ? option.id : undefined,
                })),
              }
            : component.value,
      })),
    };
  return {
    kind: "Choice",
    cardinality: input.cardinality,
    ...(input.offerOther === undefined ? {} : { offerOther: input.offerOther }),
    options: input.options.map((option) => ({
      id: isMemberId(option.id) ? option.id : undefined,
      label: option.label,
      ...(option.detail === undefined ? {} : { detail: option.detail }),
      ...(option.normal ? { normal: true } : {}),
    })),
  };
}
